/**
 * How a loop looks, and which way it runs.
 *
 * A reinforcing loop is a spiral, but nothing about the link types says whether
 * it is one you want. "Syrjäytyminen syvenee → paine kasvaa → syrjäytyminen
 * syvenee" is three cheerful `vahvistaa` edges compounding a disaster, and the
 * sign rule calls it exactly what it calls a virtuous circle. Drawn in the
 * link's own green it read as good news.
 *
 * So direction is about the content, not the arrows. And the arrows cannot even
 * give a useful first guess: a ring of `heikentää` edges dismantling something
 * harmful is a good spiral, and a ring of `vahvistaa` edges compounding a
 * disaster is a bad one. The sign rule says "reinforcing" to both.
 *
 * So nothing guesses. The AI reads the words when it proposes a loop, a person
 * can say so directly, and until one of them has, the loop is drawn as what it
 * is — unjudged — rather than coloured as if somebody had decided.
 *
 * A balancing loop is neither: it holds a thing where it is. It keeps its own
 * colour so it is not read as a mild version of either spiral.
 */

export const LOOP_TONES = {
  up: { color: '#E0A82E', label: 'Vahvistava kierre' },
  // Red, because a spiral running the wrong way is the thing on the map most
  // worth stopping — and it is not the blocker red, because a blocker is
  // something you remove and this is something you break.
  down: { color: '#E0533F', label: 'Kiihtyvä noidankehä' },
  flat: { color: '#42A5F5', label: 'Tasapainottava silmukka' },
  unknown: { color: '#8E9BB3', label: 'Silmukka — suunta arvioimatta' },
};

/**
 * What the structure alone can say: a balancing loop holds its subject where it
 * is, and that is the whole of it. Whether a reinforcing one is a virtuous
 * circle or a vicious one is not in the graph, so it stays unjudged until
 * somebody judges it.
 */
export function deriveLoopDirection({ kind }) {
  return kind === 'balancing' ? 'flat' : 'unknown';
}

export function loopTone(loop) {
  if (!loop) return LOOP_TONES.unknown;
  if (loop.kind === 'balancing') return LOOP_TONES.flat;
  return LOOP_TONES[loop.direction] || LOOP_TONES.unknown;
}

/** The two a person picks between; balancing loops are neither. */
export const LOOP_DIRECTIONS = [
  { key: 'up', label: 'Hyvä kierre', hint: 'Kierre parantaa tilannetta joka kierroksella.' },
  { key: 'down', label: 'Noidankehä', hint: 'Kierre pahentaa tilannetta joka kierroksella.' },
];

export const loopColor = (loop) => loopTone(loop).color;
